import { type SchoolYearAction, schoolYearStart, schoolYears, type Workspace } from "@classroom/domain";
import { useState } from "react";
import { useTranslation } from "./i18n";
import type { LocalRepository } from "./lib/localStore/repository";
import "./school-years.css";
export type YearDeletion = ReturnType<LocalRepository["previewSchoolYearDeletion"]>;
export default function SchoolYearSettings({
  workspace,
  selected,
  onAction,
  onSelect,
  currentYearId,
  date,
  onPreviewDelete,
  onBackup,
}: {
  workspace: Workspace;
  selected: string;
  onAction: (action: SchoolYearAction) => Promise<void>;
  onSelect: (id?: string) => Promise<void>;
  currentYearId: string;
  date: string;
  onPreviewDelete: (id: string) => Promise<YearDeletion>;
  onBackup?: () => void;
}) {
  const { t } = useTranslation("schoolYears");
  const years = schoolYears(workspace),
    year = years.find((year) => year.id === selected)!;
  const start = schoolYearStart(date) + 1;
  const nextYear = years.find((item) => item.startDate === `${start}-09-01`);
  const [source, setSource] = useState(currentYearId);
  const [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const [deletion, setDeletion] = useState<YearDeletion>(),
    [confirmation, setConfirmation] = useState(""),
    [backedUp, setBackedUp] = useState(false);
  async function run(action: SchoolYearAction) {
    setBusy(true);
    setError("");
    try {
      await onAction(action);
      setDeletion(undefined);
      setConfirmation("");
      setBackedUp(false);
    } catch (caught) {
      const code = caught instanceof Error ? caught.message : "";
      setError(
        code === "duplicate-year"
          ? t("duplicate")
          : code === "invalid-year"
            ? t("invalid")
            : code === "unsafe-delete"
              ? t("stale")
              : t("failed"),
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <section
      className="paper-card school-year-settings"
      aria-label={t("title")}
      data-save-state={busy ? "saving" : deletion ? "dirty" : "saved"}
    >
      <header>
        <h2>{t("title")}</h2>
        <span>
          {t("dateCurrent")} · {years.find((item) => item.id === currentYearId)?.name}
        </span>
      </header>
      <div className="school-year-list" role="group" aria-label={t("history")}>
        {years.map((item) => (
          <div key={item.id}>
            <strong>{item.name}</strong>
            <span>
              {t(
                item.status === "archived"
                  ? "archived"
                  : item.id === currentYearId
                    ? "open"
                    : item.startDate && item.startDate > date
                      ? "future"
                      : "editable",
              )}
            </span>
            {currentYearId === item.id && <small>{t("dateCurrent")}</small>}
            <button
              type="button"
              disabled={busy || selected === item.id}
              onClick={() => void onSelect(item.id === currentYearId ? undefined : item.id)}
            >
              {t("viewYear", { name: item.name })}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                const type = item.status === "archived" ? "reopen" : "archive";
                if (window.confirm(t(type === "archive" ? "archiveConfirm" : "reopenConfirm", { name: item.name })))
                  void run({ type, id: item.id });
              }}
            >
              {t(item.status === "archived" ? "reopen" : "archive")}
            </button>
          </div>
        ))}
      </div>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (nextYear) void onSelect(nextYear.id);
          else
            void run({ type: "create", id: crypto.randomUUID(), startYear: start, sourceYearId: source || undefined });
        }}
      >
        <fieldset disabled={busy}>
          <legend>{t("prepareNext")}</legend>
          <div className="school-year-create">
            <strong className="school-year-next-name">
              {start}–{start + 1}
            </strong>
            {!nextYear && (
              <label>
                {t("source")}
                <select value={source} onChange={(event) => setSource(event.target.value)}>
                  <option value="">{t("empty")}</option>
                  {years.map((item) => (
                    <option value={item.id} key={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <button type="submit">{t(nextYear ? "continuePreparing" : "prepareNext")}</button>
          </div>
        </fieldset>
      </form>
      {year.status === "archived" && year.id !== currentYearId && years.length > 1 && (
        <details className="school-year-delete">
          <summary>{t("delete")}</summary>
          {!deletion ? (
            <button
              type="button"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                setError("");
                try {
                  setDeletion(await onPreviewDelete(year.id));
                } catch {
                  setError(t("failed"));
                } finally {
                  setBusy(false);
                }
              }}
            >
              {t("deleteTitle")}
            </button>
          ) : (
            <div>
              <p>{t("deleteHint", { name: deletion.year.name })}</p>
              <p>{t("counts", deletion)}</p>
              {onBackup && (
                <button type="button" onClick={onBackup}>
                  {t("backupFirst")}
                </button>
              )}
              <label>
                <input type="checkbox" checked={backedUp} onChange={(event) => setBackedUp(event.target.checked)} />
                {t("backupChecked")}
              </label>
              <label>
                {t("confirmation")}
                <input
                  value={confirmation}
                  onChange={(event) => setConfirmation(event.target.value)}
                  placeholder={deletion.year.name}
                />
              </label>
              <div>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setDeletion(undefined);
                    setConfirmation("");
                    setBackedUp(false);
                  }}
                >
                  {t("cancel")}
                </button>
                <button
                  type="button"
                  className="danger-text"
                  disabled={busy || !backedUp || confirmation !== deletion.year.name}
                  onClick={() =>
                    void run({ type: "delete", id: deletion.year.id, confirmation, token: deletion.token })
                  }
                >
                  {t("confirmDelete")}
                </button>
              </div>
            </div>
          )}
        </details>
      )}
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}
